import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { usePlayerCoins } from "@/hooks/usePlayerCoins";
import { useRewardConfig } from "@/hooks/useRewardConfig";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Coins, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";

// PR-AY2a (architectural spike): use the auto-generated Supabase Tables<>
// helper instead of `Record<string, unknown>` casts. Pattern validated
// here for RewardShop + SettingsClubAndVenue; AY2b will mechanically apply
// the same approach to 5 more files. The DB types include nullability
// info (e.g. `stock: number | null`) so strictNullChecks gets the right
// info when AY3 enables the flag.
type RewardProduct = Tables<"reward_products">;

export default function RewardShop() {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { balance } = usePlayerCoins();
  const { isEnabled } = useRewardConfig();
  const qc = useQueryClient();

  const { data: products = [] } = useQuery({
    queryKey: ["reward-products", organization?.id],
    queryFn: async (): Promise<RewardProduct[]> => {
      const { data, error } = await supabase
        .from("reward_products")
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("is_active", true)
        .order("coin_cost");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id && isEnabled,
  });

  const redeemMutation = useMutation({
    mutationFn: async (product: RewardProduct) => {
      if (!user || !organization) throw new Error("Not authenticated");
      if (balance < product.coin_cost) throw new Error("Inte tillräckligt med coins");

      // Create spend transaction
      const { error: txErr } = await supabase
        .from("coin_transactions")
        .insert({
          user_id: user.id,
          organization_id: organization.id,
          amount: -product.coin_cost,
          type: "spent_reward",
          reference_id: product.id,
          description: `Inlöst: ${product.name}`,
        });
      if (txErr) throw txErr;

      // Update balance
      const { error: balErr } = await supabase
        .from("player_coins")
        .update({
          balance: balance - product.coin_cost,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .eq("organization_id", organization.id);
      if (balErr) throw balErr;

      // Decrement stock if applicable (PR-AY1 trivial fix: typeof guards
      // both null and undefined; previously the check missed undefined
      // and would propagate `NaN` from `undefined - 1` to the DB)
      if (typeof product.stock === "number") {
        await supabase
          .from("reward_products")
          .update({ stock: product.stock - 1 })
          .eq("id", product.id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["player-coins"] });
      qc.invalidateQueries({ queryKey: ["coin-transactions"] });
      qc.invalidateQueries({ queryKey: ["reward-products"] });
      toast.success(t("payments:toast.rewardRedeemed"));
    },
    onError: (e: Error) => toast.error(e.message || "Kunde inte lösa in"),
  });

  if (!isEnabled) return null;

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Gift className="h-10 w-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Inga belöningar tillgängliga just nu</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <ShoppingBag className="h-4 w-4" /> Belöningar
        </h3>
        <Badge variant="outline" className="gap-1">
          <Coins className="h-3 w-3 text-amber-500" /> {balance} coins
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {products.map((p) => (
          <Card key={p.id}>
            <CardContent className="p-4 space-y-2">
              {p.image_url && (
                <img src={p.image_url} alt={p.name} className="w-full h-24 object-cover rounded" />
              )}
              <div>
                <h4 className="font-medium text-sm">{p.name}</h4>
                {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                  {p.coin_cost} 🪙
                </span>
                {typeof p.stock === "number" && (
                  <span className="text-xs text-muted-foreground">
                    {p.stock} kvar
                  </span>
                )}
              </div>
              <Button
                size="sm"
                className="w-full"
                disabled={balance < p.coin_cost || (typeof p.stock === "number" && p.stock <= 0) || redeemMutation.isPending}
                onClick={() => redeemMutation.mutate(p)}
              >
                {balance < p.coin_cost ? "Inte tillräckligt" : "Lös in"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
